import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { UserService } from "../../../domain/user-service";
import { Router } from "@angular/router";
import { Channel } from "../../../domain/channel";
import { ChannelRepository } from "../../../domain/channel-repository";
import { Logger } from "../../../infrastructure/logger";
import { DomSanitizer } from "@angular/platform-browser";
import { UI } from "../../../services/ui.service";
import { Socket } from "../../../infrastructure/socket.service";
import { WebRTCPreview } from "../../../domain/web-r-t-c-preview";
import moment from "moment-timezone";
import { User } from "../../../domain/user";
import { Block } from "../../../domain/blocks/block";

@Component({
  selector: "channels-dashboard",
  templateUrl: "channels-dashboard.component.html",
  styleUrls: ["channels-dashboard.component.scss"],
})
export class ChannelsDashboardComponent implements OnInit, OnDestroy {
  @ViewChild("videoElem", { static: true }) videoElem: ElementRef;

  cdrInterval;
  channels: Channel[] = [];
  menuIcon = "/assets/icons/menu-more.svg";
  closeIcon = "/assets/icons/x-close-pop.svg";
  channelCodeiFrame = false;
  channelCodeJavaScript = false;
  channelCodeOptions = [
    { name: "IFrame", value: 0 },
    { name: "JavaScript", value: 1 },
  ];
  selectedChannel: Channel = null;
  needToConfirm = false;
  hasPermissionsToCreate: boolean;
  time;
  chosenQualities: { name: string; channel: Channel }[] = [];
  showQualitySettings = false;
  connectionId: string;
  channelPreviews = {};
  currentUser: User;
  constructor(
    private userService: UserService,
    private router: Router,
    private channelRepository: ChannelRepository,
    private socket: Socket,
    private changeDetectorRef: ChangeDetectorRef,
    private logger: Logger,
    private sanitizer: DomSanitizer,
    private ui: UI,
  ) {}

  togglePlay() {
    if (this.videoElem.nativeElement.paused) {
      this.videoElem.nativeElement.play();
    } else {
      this.videoElem.nativeElement.pause();
    }
  }

  isPaused() {
    return this.videoElem.nativeElement.paused;
  }

  isMuted(channel) {
    return channel.isMuted;
  }

  getChannelBoxStyle(channel: Channel): { [key: string]: string } {
    return channel.placeholderImage
      ? { 'background-image': 'url(' + channel.placeholderImage + ')' }
      : {};
  }

  getChannelTime(channel: Channel) {
    const index = this.userService
      .getCurrentUser()
      .timezones.findIndex((x) => x.id === channel.timezoneId);
    const currentTimeZone =
      this.userService.getCurrentUser().timezones[index].value;
    const currentTimeWithZone = moment().tz(currentTimeZone);
    return currentTimeWithZone.clone().format("HH:mm:ss z");
  }

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.cdrInterval = window.setInterval(() => {
      this.changeDetectorRef.markForCheck();
    }, 1000);
    this.connectionId = this.socket.connectionId;
    this.channelRepository.getTenantChannels().then((channels) => {
      this.channels = channels.filter((c) => !c.isBlock);
      this.chosenQualities = this.channels.map((channel) => {
        return { name: "240p", channel: channel };
      });
      channels.forEach((channel) => {
        if (channel.isActive) {
          this.createChannelWebRTCPreview(channel);
        }
      });
      this.changeDetectorRef.markForCheck();
    });
    this.userService.getCurrentChannel().then((channel) => {
      if (channel) {
        this.selectedChannel = channel;
      }
    });
    this.hasPermissionsToCreate =
      this.userService.getCurrentUser().canCreateChannels;
    const self = this;
    this.channelRepository.channelDeleted.subscribe((id) => {
      if (id >= 0) {
        self.channels.splice(
          self.channels.findIndex((x) => x.id === id),
          1,
        );
        self.changeDetectorRef.markForCheck();
      }
    });
    document.addEventListener(
      "webkitfullscreenchange",
      self.onFullscreenChange.bind(this),
    );
  }

  onFullscreenChange() {
    const channel = this.selectedChannel;
    if (!!channel) {
      if (this.isFullScreen(channel.id)) {
        this.changeQuality(channel.previewUrls[0].clientId, channel);
      } else {
        this.changeQuality("240p", channel);
      }
    }
  }

  isChosenQuality(quality: string, channel: Channel) {
    const index = this.chosenQualities.findIndex(
      (x) => x.channel.id === channel.id,
    );
    if (index >= 0) {
      return this.chosenQualities[index].name === quality;
    }
    return false;
  }

  changeQuality(quality: string, channel: Channel) {
    const index = this.chosenQualities.findIndex(
      (x) => x.channel.id === channel.id,
    );
    if (index >= 0) {
      this.chosenQualities[index].name = quality;
    }
    this.changeDetectorRef.markForCheck();
  }

  getAvailableQualities(channel: Channel) {
    return channel.previewUrls.map((x) => x.clientId);
  }

  onEdit(channel, menuPopover) {
    menuPopover.hide();
    this.router.navigate([`channels/edit/${channel.id}`]);
  }

  onDuplicate(channel, menuPopover) {
    menuPopover.hide();
  }

  onDelete(channel, menuPopover) {
    if (channel.isActive) {
      this.ui.notify(this.ui.EVENTS.ShowAlert, {
        title: "Can't delete channel!",
        message: "Activated channels can not be deleted",
      });
    } else {
      this.ui.notify(this.ui.EVENTS.ShowConfirm, {
        title: "Delete channel",
        message: "Are you sure about that?",
        buttons: [
          {
            title: "Yes",
            callback: () => {
              this.channelRepository.deleteChannel(channel);
            },
          },
          {
            title: "No",
            callback: () => {},
          },
        ],
      });
    }
    menuPopover.hide();
  }

  onMenuIconClick(e, menuPopover) {
    this.needToConfirm = false;
    this.userService.setCurrentChannel(this.selectedChannel);
    menuPopover.toggle(e);
    e.stopImmediatePropagation();
  }

  onChannelCodeOptionSelect(option) {
    this.channelCodeiFrame = option.value === 0;
    this.channelCodeJavaScript = option.value === 1;
  }

  formatDate(channel: Channel) {
    return channel.createdDate.format("MM/DD/YYYY");
  }

  isFirstTimer() {
    return this.channels.length === 0;
  }

  toggleActiveState(channel, menuPopover) {
    const state = !channel.isActive;
    this.needToConfirm = false;
    menuPopover.hide();
    this.changeDetectorRef.markForCheck();
    this.channelRepository
      .setChannelActiveState(channel.id, state)
      .then(() => {
        channel.isActive = state;
        if (channel.isActive) {
          this.createChannelWebRTCPreview(channel);
        }
        this.changeDetectorRef.markForCheck();
      })
      .catch((error) => {
        this.logger.warn(
          "could not set channel active state. channel " +
            channel.id +
            "; active: " +
            state +
            "; error: " +
            error,
        );
      });
  }

  setCurrentChannel(channel) {
    this.selectedChannel = channel;
    this.userService.setCurrentChannel(channel);
  }

  channelToggle(channel, menuPopover) {
    if (channel.isActive) {
      this.needToConfirm = true;
    } else {
      this.toggleActiveState(channel, menuPopover);
    }
  }

  toggleMute(channel) {
    channel.isMuted = !channel.isMuted;
  }

  getSvg(channel) {
    if (channel.isMuted) {
      return "/assets/icons/Player-soundOff.svg";
    } else {
      return "/assets/icons/sound-on.svg";
    }
  }

  getChannelPreview(channel: Channel): WebRTCPreview {
    if (channel && this.channelPreviews[channel.id]) {
      return this.channelPreviews[channel.id];
    }
    return null;
  }

  onRefreshChannelPreview(channel: Channel) {
    this.createChannelWebRTCPreview(channel);
  }
  createChannelWebRTCPreview(channel) {
    if (channel) {
      this.socket
        .createChannelWebRTCPreview(channel)
        .then((preview: WebRTCPreview) => {
          this.channelPreviews[channel.id] = preview;
        });
    }
  }
  enterFullscreen(channel) {
    const fullscreen = document.getElementById(channel.id);
    (fullscreen as any).webkitRequestFullScreen();
    this.changeDetectorRef.markForCheck();
  }

  exitFullscreen() {
    (document as any).webkitExitFullscreen();
    this.changeDetectorRef.markForCheck();
  }

  isFullScreen(channelId: number) {
    if (this.selectedChannel && channelId === this.selectedChannel.id) {
      return (document as any).webkitIsFullScreen;
    }
    return false;
  }

  ngOnDestroy() {
    document.removeEventListener(
      "webkitfullscreenchange",
      this.onFullscreenChange,
    );
    if (!!this.cdrInterval) {
      clearInterval(this.cdrInterval);
    }
  }
  buildSmartScheduleUrl(channel: Channel) {
    return `/smart-planner/?c=${channel.id}`;
  }
}
